import React, { useEffect, useRef, useState } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TournamentActions from './TournamentActions';
import ToggleActive from './ToggleActive';
import Tooltip from '@mui/material/Tooltip';

export interface Tournament {
  id: string;
  name: string;
  username?: string;
  avatar: string;
  city: string;
  state: string;
  startDate: string;
  active: boolean;
  stats?: {
    totalCategorias: number;
    categoriasAtivas: number;
    totalInscricoes: number;
    inscricoesNaoPagas: number;
    totalPartidas: number;
    partidasSemResultado: number;
  };
}

export interface TournamentTableRowProps {
  tournament: Tournament;
  onToggle: (id: string) => void;
  onEdit: (id: string) => void;
  onView: (id: string) => void;
  onPayments?: (id: string) => void;
  onReportsInscriptions?: (id: string) => void;
  onReportsFinancial?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
}

const TournamentTableRow: React.FC<TournamentTableRowProps> = ({
  tournament,
  onToggle,
  onEdit,
  onView,
  onPayments,
  onReportsInscriptions,
  onReportsFinancial,
  onDelete,
  onDuplicate,
}) => {
  const [showReports, setShowReports] = useState(false);
  const reportsRef = useRef<HTMLDivElement>(null);

  const toggleReports = () => setShowReports(prev => !prev);

  const handleReportsInscriptions = () => {
    onReportsInscriptions && onReportsInscriptions(tournament.id);
    setShowReports(false);
  };

  const handleReportsFinancial = () => {
    onReportsFinancial && onReportsFinancial(tournament.id);
    setShowReports(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (reportsRef.current && !reportsRef.current.contains(event.target as Node)) {
        setShowReports(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onClickRow = () => {
    onView(tournament.id);
  };

  return (
    <TableRow hover  >
      <TableCell>
        <div className="d-flex align-items-center cursor-pointer" onClick={onClickRow} >
          <img
            src={tournament.avatar}
            alt={tournament.name}
            className="rounded-circle me-2"
            width={40}
            height={40}
          />
          <div>
            <div className="fw-bold">{tournament.name}</div>
            <div className="text-muted font-xsss">
              {tournament.city}-{tournament.state}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        {new Date(tournament.startDate).toLocaleDateString('pt-BR')}
      </TableCell>
      <TableCell>
        {tournament.stats ? (
          <Tooltip title={`${tournament.stats.totalCategorias} criadas / ${tournament.stats.categoriasAtivas} ativas`} placement="top">
            <div className="font-xsss">
              <div>{tournament.stats.totalCategorias} / {tournament.stats.categoriasAtivas}</div>
            </div>
          </Tooltip>
        ) : '-'}
      </TableCell>
      <TableCell>
        {tournament.stats ? (
          <Tooltip title={`${tournament.stats.totalInscricoes} criadas / ${tournament.stats.inscricoesNaoPagas} não pagas`} placement="top">
            <div className="font-xsss">
              <div>{tournament.stats.totalInscricoes} / {tournament.stats.inscricoesNaoPagas}</div>
            </div>
          </Tooltip>
        ) : '-'}
      </TableCell>
      <TableCell>
        {tournament.stats ? (
          <Tooltip title={`${tournament.stats.totalPartidas} criadas / ${tournament.stats.partidasSemResultado} sem resultado`} placement="top">
            <div className="font-xsss">
              <div>{tournament.stats.totalPartidas} / {tournament.stats.partidasSemResultado}</div>
            </div>
          </Tooltip>
        ) : '-'}
      </TableCell>
      <TableCell>
        <div className="d-flex align-items-center gap-2">
          <TournamentActions
            onView={() => onView(tournament.id)}
            onPayments={() => onPayments && onPayments(tournament.id)}
            onEdit={() => onEdit(tournament.id)}
          />
          {onDuplicate && (
            <button
              type="button"
              className="btn btn-link p-0 text-secondary"
              onClick={() => onDuplicate(tournament.id)}
            >
              <Tooltip title="Duplicar Torneio" placement="top">
                <i className="bi bi-copy" />
              </Tooltip>
            </button>
          )}
          <div className="position-relative" ref={reportsRef}>
            <div
              className='cursor-pointer'
              onClick={toggleReports}
            >
              <Tooltip title="Relatórios" placement="top">
                <i className="bi bi-printer" />
              </Tooltip>
            </div>
            {showReports && (
              <div className="position-absolute mt-2 bg-white border border-dark rounded" style={{ zIndex: 1000 }}>
                <button
                  className="btn w-100 text-start text-nowrap"
                  onClick={handleReportsInscriptions}
                >
                  <i className="bi bi-printer" />
                  Relatório de Inscritos
                </button>
                <hr className="my-0" />
                <button
                  className="btn w-100 text-start"
                  onClick={handleReportsFinancial}
                >
                  <i className="bi bi-printer" />
                  <span>
                    Relatório Financeiro
                  </span>
                </button>
              </div>
            )}
          </div>
          {onDelete && (
            <button
              type="button"
              className="btn btn-link p-0 text-danger"
              onClick={() => onDelete(tournament.id)}
            >
              <Tooltip title="Deletar Torneio" placement="top">
                <i className="bi bi-trash" />
              </Tooltip>
            </button>
          )}
          <ToggleActive
            checked={tournament.active}
            onChange={() => onToggle(tournament.id)}
          />
        </div>
      </TableCell>
    </TableRow>
  );
};

export default TournamentTableRow;
